import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const BookingsManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  
  // Modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [refundOption, setRefundOption] = useState('');
  const [newDate, setNewDate] = useState('');
  const [processing, setProcessing] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);

  // Dropdown state
  const [openDropdown, setOpenDropdown] = useState(null);

  const refundOptions = [
    { value: 'full_refund', label: 'Full Refund', description: 'Return 100% of payment' },
    { value: 'partial_refund_50', label: '50% Refund', description: 'Return 50% of payment' },
    { value: 'credit_voucher', label: 'Credit Voucher', description: 'Issue credit for future booking' },
    { value: 'no_refund', label: 'No Refund', description: 'Cancellation without refund (late notice)' },
    { value: 'reschedule_only', label: 'Reschedule Only', description: 'Move to different date, no refund' }
  ];

  useEffect(() => {
    fetchBookings();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
      } else {
        setError('Failed to fetch bookings');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDates = async (adventureId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/availability/${adventureId}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableDates(data.availability || []);
      }
    } catch (err) {
      console.error('Error fetching dates:', err);
    }
  };

  const handleCancelClick = (booking) => {
    setSelectedBooking(booking);
    setCancelReason('');
    setRefundOption('');
    setShowCancelModal(true);
    setOpenDropdown(null);
  };

  const handleRescheduleClick = async (booking) => {
    setSelectedBooking(booking);
    setNewDate('');
    await fetchAvailableDates(booking.adventure_id);
    setShowRescheduleModal(true);
    setOpenDropdown(null);
  };

  const handleCancelBooking = async () => {
    if (!refundOption) {
      alert('Please select a refund option');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${selectedBooking.booking_reference}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reason: cancelReason,
          refund_option: refundOption
        })
      });

      if (response.ok) {
        setShowCancelModal(false);
        fetchBookings();
        alert('Booking cancelled successfully');
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to cancel booking');
      }
    } catch (err) {
      alert('Connection error');
    } finally {
      setProcessing(false);
    }
  };

  const handleRescheduleBooking = async () => {
    if (!newDate) {
      alert('Please select a new date');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${selectedBooking.booking_reference}/reschedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          new_date: newDate
        })
      });

      if (response.ok) {
        setShowRescheduleModal(false);
        fetchBookings();
        alert('Booking rescheduled successfully');
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to reschedule booking');
      }
    } catch (err) {
      alert('Connection error');
    } finally {
      setProcessing(false);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'all') return true;
    if (filter === 'cancelled') return booking.booking_status === 'cancelled';
    if (filter === 'paid') return booking.payment_status === 'paid' && booking.booking_status !== 'cancelled';
    if (filter === 'pending') return booking.payment_status === 'pending';
    return booking.booking_status === filter || booking.payment_status === filter;
  });

  const StatusBadge = ({ status }) => {
    const statusColors = {
      'completed': 'bg-green-100 text-green-800',
      'paid': 'bg-green-100 text-green-800',
      'confirmed': 'bg-blue-100 text-blue-800',
      'pending_payment': 'bg-yellow-100 text-yellow-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'cancelled': 'bg-red-100 text-red-800',
      'rescheduled': 'bg-purple-100 text-purple-800'
    };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  const RefundBadge = ({ refundOption }) => {
    if (!refundOption) return null;
    
    const labels = {
      'full_refund': 'Full Refund',
      'partial_refund_50': '50% Refund',
      'credit_voucher': 'Credit Voucher',
      'no_refund': 'No Refund',
      'reschedule_only': 'Reschedule Only'
    };

    return (
      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 ml-1">
        {labels[refundOption] || refundOption}
      </span>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Bookings Management</h1>
          <p className="text-gray-600 mt-2">Manage all adventure bookings</p>
        </div>
        <div className="flex space-x-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Bookings</option>
            <option value="paid">Paid / Confirmed</option>
            <option value="pending">Pending Payment</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-lg shadow overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Adventure
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Group Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredBookings.map((booking, index) => (
                <motion.tr
                  key={booking.booking_reference || booking.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`hover:bg-gray-50 ${booking.booking_status === 'cancelled' ? 'bg-red-50' : ''}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {booking.customer_name || `${booking.customer_first_name || ''} ${booking.customer_last_name || ''}`.trim() || '-'}
                      </p>
                      <p className="text-sm text-gray-500">{booking.customer_email}</p>
                      <p className="text-sm text-gray-500">{booking.customer_phone}</p>
                      <p className="text-xs text-gray-400 font-mono">{booking.booking_reference}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <p className="text-sm text-gray-900">{booking.adventure_title || booking.adventure_id}</p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <p className="text-sm text-gray-900">
                      {booking.booking_date ? new Date(booking.booking_date + 'T00:00:00').toLocaleDateString('en-AU', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        timeZone: 'Australia/Sydney'
                      }) : booking.selected_date || '-'}
                    </p>
                    {booking.original_date && booking.original_date !== booking.booking_date && (
                      <p className="text-xs text-purple-600">
                        (Rescheduled from {booking.original_date})
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <p className="text-sm text-gray-900">{booking.participants || booking.group_size || '-'}</p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <p className="text-sm font-medium text-gray-900">
                      ${booking.total_price || booking.total_amount || 0}
                    </p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <StatusBadge status={booking.booking_status === 'cancelled' ? 'cancelled' : (booking.payment_status || booking.booking_status)} />
                      {booking.booking_status === 'cancelled' && (
                        <RefundBadge refundOption={booking.refund_option} />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {booking.booking_status !== 'cancelled' ? (
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(openDropdown === booking.booking_reference ? null : booking.booking_reference);
                          }}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Edit
                          <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {/* Dropdown Menu */}
                        {openDropdown === booking.booking_reference && (
                          <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                            <div className="py-1">
                              <button
                                onClick={() => handleRescheduleClick(booking)}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <svg className="mr-3 h-4 w-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Reschedule
                              </button>
                              <button
                                onClick={() => handleCancelClick(booking)}
                                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                              >
                                <svg className="mr-3 h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Cancel Booking
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-gray-400 text-sm">
                        {booking.cancellation_reason && (
                          <span title={booking.cancellation_reason}>Reason: {booking.cancellation_reason.substring(0, 20)}...</span>
                        )}
                      </div>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredBookings.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No bookings found</p>
          </div>
        )}
      </motion.div>

      {/* Cancel Modal */}
      <AnimatePresence>
        {showCancelModal && selectedBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCancelModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Cancel Booking</h3>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Customer:</strong> {selectedBooking.customer_name}<br/>
                  <strong>Adventure:</strong> {selectedBooking.adventure_title}<br/>
                  <strong>Date:</strong> {selectedBooking.booking_date}<br/>
                  <strong>Amount:</strong> ${selectedBooking.total_price}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Refund Option <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {refundOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                        refundOption === option.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="refundOption"
                        value={option.value}
                        checked={refundOption === option.value}
                        onChange={(e) => setRefundOption(e.target.value)}
                        className="mt-1 mr-3"
                      />
                      <div>
                        <p className="font-medium text-gray-900">{option.label}</p>
                        <p className="text-sm text-gray-500">{option.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cancellation Reason (optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={2}
                  placeholder="Enter reason for cancellation..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Go Back
                </button>
                <button
                  onClick={handleCancelBooking}
                  disabled={processing || !refundOption}
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {processing ? 'Processing...' : 'Confirm Cancellation'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reschedule Modal */}
      <AnimatePresence>
        {showRescheduleModal && selectedBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRescheduleModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Reschedule Booking</h3>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Customer:</strong> {selectedBooking.customer_name}<br/>
                  <strong>Adventure:</strong> {selectedBooking.adventure_title}<br/>
                  <strong>Current Date:</strong> {selectedBooking.booking_date}
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select New Date <span className="text-red-500">*</span>
                </label>
                
                {availableDates.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {availableDates
                      .filter(d => d.date !== selectedBooking.booking_date && d.available_spots > 0)
                      .map((dateInfo) => (
                        <label
                          key={dateInfo.date}
                          className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                            newDate === dateInfo.date
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name="newDate"
                              value={dateInfo.date}
                              checked={newDate === dateInfo.date}
                              onChange={(e) => setNewDate(e.target.value)}
                              className="mr-3"
                            />
                            <span className="font-medium">
                              {new Date(dateInfo.date + 'T00:00:00').toLocaleDateString('en-AU', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                timeZone: 'Australia/Sydney'
                              })}
                            </span>
                          </div>
                          <span className="text-sm text-green-600">
                            {dateInfo.available_spots} spots
                          </span>
                        </label>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500">No other available dates found.</p>
                    <input
                      type="date"
                      value={newDate}
                      onChange={(e) => setNewDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="mt-3 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowRescheduleModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRescheduleBooking}
                  disabled={processing || !newDate}
                  className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {processing ? 'Processing...' : 'Confirm Reschedule'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BookingsManagement;
